import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { storage, HabitTracker as HabitType } from '../utils/storageManager';
import { Target, Plus, CheckCircle, Circle, Trash2, Edit2, Save, X, Calendar, TrendingUp } from 'lucide-react';
import toast from 'react-hot-toast';

/**
 * Habit Tracker Component
 * 
 * Practical habit tracking for spiritual practices
 * - Create and track daily/weekly habits
 * - Visual streak tracking
 * - Completion history calendar
 * - Progress analytics
 */

const HabitTracker: React.FC = () => {
  const [habits, setHabits] = useState<HabitType[]>([]);
  const [isCreating, setIsCreating] = useState(false);
  const [editingHabit, setEditingHabit] = useState<HabitType | null>(null);
  
  const [newHabit, setNewHabit] = useState({
    name: '',
    description: '',
    frequency: 'daily' as 'daily' | 'weekly' | 'custom'
  });

  useEffect(() => {
    loadHabits();
  }, []);

  const loadHabits = async () => {
    try {
      const loadedHabits = await storage.getHabits();
      setHabits(loadedHabits);
    } catch (error) {
      console.error('Failed to load habits:', error);
      toast.error('Failed to load habits');
    }
  };

  const handleCreateHabit = async () => {
    if (!newHabit.name) {
      toast.error('Please enter a habit name');
      return;
    }

    try {
      if (editingHabit) {
        // Update existing habit
        await storage.updateHabit(editingHabit.id, {
          name: newHabit.name,
          description: newHabit.description,
          frequency: newHabit.frequency
        });
        toast.success('✨ Habit updated!');
      } else {
        // Create new habit
        await storage.saveHabit({
          name: newHabit.name,
          description: newHabit.description,
          frequency: newHabit.frequency,
          completions: []
        });
        toast.success('✨ Habit created!');
      }

      setNewHabit({ name: '', description: '', frequency: 'daily' });
      setIsCreating(false);
      setEditingHabit(null);
      loadHabits();
    } catch (error) {
      console.error('Failed to save habit:', error);
      toast.error('Failed to save habit');
    }
  };

  const handleCompleteHabit = async (habit: HabitType) => {
    const today = new Date().toISOString().split('T')[0];
    const isCompletedToday = habit.completions.includes(today);

    if (isCompletedToday) {
      toast('Already completed today!', { icon: '✓' });
      return;
    }

    try {
      await storage.completeHabit(habit.id);
      toast.success(`✨ ${habit.name} completed!`);
      loadHabits();
    } catch (error) {
      console.error('Failed to complete habit:', error);
      toast.error('Failed to complete habit');
    }
  };

  const handleEditHabit = (habit: HabitType) => {
    setEditingHabit(habit);
    setNewHabit({
      name: habit.name,
      description: habit.description,
      frequency: habit.frequency
    });
    setIsCreating(true);
  };

  const handleDeleteHabit = async (id: string) => {
    if (!window.confirm('Delete this habit? This cannot be undone.')) return;

    try {
      await storage.deleteHabit(id);
      toast.success('Habit deleted');
      loadHabits();
    } catch (error) {
      console.error('Failed to delete habit:', error);
      toast.error('Failed to delete habit');
    }
  };

  const getCompletionRate = (habit: HabitType): number => {
    if (habit.completions.length === 0) return 0;
    
    const daysSinceCreation = Math.ceil((Date.now() - new Date(habit.createdAt).getTime()) / (1000 * 60 * 60 * 24));
    return Math.round((habit.completions.length / Math.max(daysSinceCreation, 1)) * 100);
  };

  const isCompletedToday = (habit: HabitType): boolean => {
    const today = new Date().toISOString().split('T')[0];
    return habit.completions.includes(today);
  };

  // Get last 7 days for mini calendar
  const getLast7Days = (): string[] => {
    const days: string[] = [];
    for (let i = 6; i >= 0; i--) {
      const date = new Date();
      date.setDate(date.getDate() - i);
      days.push(date.toISOString().split('T')[0]);
    }
    return days;
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      style={{ width: '100%', maxWidth: '100%', margin: '0 auto' }}
    >
      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '2rem'
      }}>
        <div>
          <h2 style={{ fontSize: '1.75rem', fontWeight: '700', color: '#f59e0b', marginBottom: '0.5rem' }}>
            <Target size={28} style={{ display: 'inline', marginRight: '0.5rem', verticalAlign: 'middle' }} />
            Habit Tracker
          </h2>
          <p style={{ color: '#64748b', fontSize: '0.875rem' }}>
            {habits.length} habits • Build consistent spiritual practices
          </p>
        </div>

        {!isCreating && (
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setIsCreating(true)}
            style={{
              padding: '10px 20px',
              background: 'linear-gradient(135deg, #f59e0b, #d97706)',
              border: 'none',
              borderRadius: '8px',
              color: 'white',
              fontWeight: '600',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}
          >
            <Plus size={20} />
            New Habit
          </motion.button>
        )}
      </div>

      {/* Create/Edit Form */}
      <AnimatePresence>
        {isCreating && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            style={{
              background: 'rgba(30, 41, 59, 0.9)',
              borderRadius: '16px',
              padding: '2rem',
              marginBottom: '2rem',
              border: '1px solid rgba(245, 158, 11, 0.3)'
            }}
          >
            <h3 style={{ color: '#f59e0b', marginBottom: '1.5rem' }}>
              {editingHabit ? 'Edit Habit' : 'Create New Habit'}
            </h3>

            <input
              type="text"
              placeholder="Habit name (e.g., Morning Meditation)"
              value={newHabit.name}
              onChange={(e) => setNewHabit({ ...newHabit, name: e.target.value })}
              style={{
                width: '100%',
                padding: '12px',
                background: 'rgba(15, 23, 42, 0.8)',
                border: '2px solid rgba(245, 158, 11, 0.3)',
                borderRadius: '8px',
                color: '#f1f5f9',
                fontSize: '1rem',
                marginBottom: '1rem'
              }}
            />

            <textarea
              placeholder="Description (optional)"
              value={newHabit.description}
              onChange={(e) => setNewHabit({ ...newHabit, description: e.target.value })}
              rows={3}
              style={{
                width: '100%',
                padding: '12px',
                background: 'rgba(15, 23, 42, 0.8)',
                border: '2px solid rgba(245, 158, 11, 0.3)',
                borderRadius: '8px',
                color: '#f1f5f9',
                fontSize: '1rem',
                marginBottom: '1rem',
                resize: 'vertical',
                fontFamily: 'inherit'
              }}
            />

            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ color: '#f59e0b', fontSize: '0.875rem', marginBottom: '0.5rem', display: 'block' }}>
                Frequency
              </label>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                {['daily', 'weekly', 'custom'].map(freq => (
                  <motion.button
                    key={freq}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => setNewHabit({ ...newHabit, frequency: freq as any })}
                    style={{
                      padding: '8px 16px',
                      background: newHabit.frequency === freq ? 'rgba(245, 158, 11, 0.3)' : 'rgba(100, 116, 139, 0.1)',
                      border: newHabit.frequency === freq ? '2px solid #f59e0b' : '2px solid rgba(100, 116, 139, 0.2)',
                      borderRadius: '8px',
                      color: newHabit.frequency === freq ? '#f59e0b' : '#64748b',
                      cursor: 'pointer',
                      fontSize: '0.875rem',
                      textTransform: 'capitalize'
                    }}
                  >
                    {freq}
                  </motion.button>
                ))}
              </div>
            </div>

            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={handleCreateHabit}
                style={{
                  flex: 1,
                  padding: '10px',
                  background: 'linear-gradient(135deg, #10b981, #059669)',
                  border: 'none',
                  borderRadius: '8px',
                  color: 'white',
                  fontWeight: '600',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem'
                }}
              >
                <Save size={18} />
                {editingHabit ? 'Update Habit' : 'Create Habit'}
              </motion.button>

              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => {
                  setIsCreating(false);
                  setEditingHabit(null);
                  setNewHabit({ name: '', description: '', frequency: 'daily' });
                }}
                style={{
                  padding: '10px 20px',
                  background: 'rgba(100, 116, 139, 0.2)',
                  border: '2px solid rgba(100, 116, 139, 0.3)',
                  borderRadius: '8px',
                  color: '#64748b',
                  fontWeight: '600',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
              >
                <X size={18} />
                Cancel
              </motion.button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Habits List */}
      <div style={{ display: 'grid', gap: '1rem' }}>
        {habits.map((habit, index) => {
          const completed = isCompletedToday(habit);
          const completionRate = getCompletionRate(habit);
          const last7Days = getLast7Days();

          return (
            <motion.div
              key={habit.id}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.05 }}
              className="fr00t-card"
              style={{
                background: completed ? 'rgba(16, 185, 129, 0.1)' : 'rgba(30, 41, 59, 0.8)',
                borderColor: completed ? 'rgba(16, 185, 129, 0.3)' : 'rgba(245, 158, 11, 0.3)'
              }}
            >
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'start',
                marginBottom: '1rem'
              }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
                    <motion.button
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                      onClick={() => handleCompleteHabit(habit)}
                      style={{
                        padding: '4px',
                        background: completed ? 'rgba(16, 185, 129, 0.3)' : 'rgba(100, 116, 139, 0.1)',
                        border: completed ? '2px solid #10b981' : '2px solid rgba(100, 116, 139, 0.3)',
                        borderRadius: '50%',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      {completed ? (
                        <CheckCircle size={20} color="#10b981" />
                      ) : (
                        <Circle size={20} color="#64748b" />
                      )}
                    </motion.button>

                    <div>
                      <h3 style={{
                        color: completed ? '#10b981' : '#f59e0b',
                        textDecoration: completed ? 'line-through' : 'none',
                        marginBottom: '0.25rem'
                      }}>
                        {habit.name}
                      </h3>
                      {habit.description && (
                        <p style={{ color: '#64748b', fontSize: '0.875rem', margin: 0 }}>
                          {habit.description}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Stats */}
                  <div style={{
                    display: 'flex',
                    gap: '1.5rem',
                    fontSize: '0.75rem',
                    color: '#64748b',
                    marginTop: '0.75rem'
                  }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                      <TrendingUp size={12} />
                      {habit.streak} day streak
                    </span>
                    <span>
                      {completionRate}% completion rate
                    </span>
                    <span style={{ textTransform: 'capitalize' }}>
                      {habit.frequency}
                    </span>
                  </div>

                  {/* Mini Calendar */}
                  <div style={{ display: 'flex', gap: '4px', marginTop: '0.75rem' }}>
                    {last7Days.map(date => {
                      const isCompleted = habit.completions.includes(date);
                      const isToday = date === new Date().toISOString().split('T')[0];
                      
                      return (
                        <div
                          key={date}
                          title={new Date(date).toLocaleDateString()}
                          style={{
                            width: '24px',
                            height: '24px',
                            borderRadius: '4px',
                            background: isCompleted ? '#10b981' : 'rgba(100, 116, 139, 0.2)',
                            border: isToday ? '2px solid #f59e0b' : 'none',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          {isCompleted && <CheckCircle size={14} color="white" />}
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <motion.button
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    onClick={() => handleEditHabit(habit)}
                    style={{
                      padding: '6px',
                      background: 'rgba(99, 102, 241, 0.2)',
                      border: '1px solid rgba(99, 102, 241, 0.3)',
                      borderRadius: '6px',
                      color: '#6366f1',
                      cursor: 'pointer'
                    }}
                  >
                    <Edit2 size={14} />
                  </motion.button>

                  <motion.button
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    onClick={() => handleDeleteHabit(habit.id)}
                    style={{
                      padding: '6px',
                      background: 'rgba(239, 68, 68, 0.2)',
                      border: '1px solid rgba(239, 68, 68, 0.3)',
                      borderRadius: '6px',
                      color: '#ef4444',
                      cursor: 'pointer'
                    }}
                  >
                    <Trash2 size={14} />
                  </motion.button>
                </div>
              </div>
            </motion.div>
          );
        })}

        {habits.length === 0 && !isCreating && (
          <div style={{
            textAlign: 'center',
            padding: '4rem 2rem',
            color: '#64748b'
          }}>
            <Target size={60} style={{ margin: '0 auto 1rem', opacity: 0.5 }} />
            <h3 style={{ marginBottom: '0.5rem', color: '#f59e0b' }}>
              No habits yet
            </h3>
            <p>
              Create your first habit to start building consistent spiritual practices
            </p>
          </div>
        )}
      </div>

      {/* Sacred Numerology */}
      <motion.div
        className="fr00t-card"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
        style={{
          marginTop: '2rem',
          textAlign: 'center',
          background: 'rgba(245, 158, 11, 0.1)',
          borderColor: 'rgba(245, 158, 11, 0.3)'
        }}
      >
        <div style={{
          fontSize: '0.75rem',
          color: '#f59e0b',
          fontFamily: 'monospace'
        }}>
          "Small daily improvements over time lead to stunning results." - Fr00t Wisdom
        </div>
      </motion.div>
    </motion.div>
  );
};

export default HabitTracker;

